/** R57: a layered tempera landscape beneath the pen-drawn inhabited margins.
 * The winding route is continuous; all grain lives in fixed page/world space. */
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';
import {fbm2o,rnd2,sdTaper,vnoise} from './lib';

const landscapeTree=(p:d.v2f,seed:number,kind:number)=>{
 'use gpu';
 const lean=(rnd2(d.vec2f(seed,9))-.5)*.13;
 const q=d.vec2f(p.x-p.y*lean,p.y);
 let crown=d.f32(10);
 if(kind===1){
  const width=.12*(1-std.pow(std.clamp(q.y/.9,0,1),1.6));
  crown=std.max(std.abs(q.x)-width-(.012*std.sin(q.y*58+seed)),std.max(.10-q.y,q.y-.96));
 }else if(kind===2){
  crown=d.f32(10);
 }else{
  for(let leaf=0;leaf<5;leaf++){
   const k=d.f32(leaf);
   const c=d.vec2f(std.sin(k*2.4+seed)*.16,.54+std.cos(k*1.7+seed)*.12);
   const oval=std.length(q.sub(c).div(d.vec2f(.18,.22)))-1;
   crown=std.min(crown,oval*.18);
  }
 }
 const trunk=sdTaper(q,d.vec2f(0,0),d.vec2f(-.025,.70),.032,.007);
 let branch=trunk;
 for(let twig=0;twig<4;twig++){
  const k=d.f32(twig);
  const side=std.select(d.f32(-1),d.f32(1),(twig%2)===0);
  branch=std.min(branch,sdTaper(q,d.vec2f(-.008,.20+k*.12),d.vec2f(side*(.10+k*.026),.41+k*.13),.012,.003));
 }
 return d.vec2f(crown,branch);
};

export const expeditionGround=(uv:d.v2f,px:d.v2f,time:number,params:d.v4f)=>{
 'use gpu';
 const seed=params.x*.001;
 const biome=d.u32(params.y);
 const weather=d.u32(params.z);
 const travel=params.w;
 const aa=std.max(std.fwidth(uv.x),std.fwidth(uv.y));
 const ink=d.vec3f(.041,.027,.019);
 const paper=d.vec3f(.80,.68,.43);
 let sky=d.vec3f(.044,.070,.17);
 let far=d.vec3f(.25,.33,.28);
 let hill=d.vec3f(.30,.35,.15);
 let earth=d.vec3f(.32,.32,.13);
 let leaf=d.vec3f(.085,.15,.062);
 let water=d.vec3f(.17,.32,.34);
 if(biome===1){far=d.vec3f(.19,.28,.22);hill=d.vec3f(.18,.26,.12);earth=d.vec3f(.22,.24,.10);leaf=d.vec3f(.046,.11,.064);}
 if(biome===2){sky=d.vec3f(.07,.125,.13);far=d.vec3f(.23,.34,.245);hill=d.vec3f(.14,.25,.125);earth=d.vec3f(.19,.235,.125);leaf=d.vec3f(.035,.12,.065);water=d.vec3f(.12,.24,.23);}
 if(biome===3){sky=d.vec3f(.16,.22,.28);far=d.vec3f(.48,.38,.21);hill=d.vec3f(.55,.39,.16);earth=d.vec3f(.49,.32,.13);leaf=d.vec3f(.23,.18,.07);}
 if(biome===4||biome===9){sky=d.vec3f(.07,.13,.20);far=d.vec3f(.43,.48,.40);hill=d.vec3f(.30,.38,.34);earth=d.vec3f(.43,.45,.34);leaf=d.vec3f(.06,.12,.095);water=d.vec3f(.20,.34,.40);}
 if(biome===5){sky=d.vec3f(.10,.19,.27);far=d.vec3f(.29,.40,.37);hill=d.vec3f(.37,.40,.20);earth=d.vec3f(.47,.40,.20);leaf=d.vec3f(.10,.16,.072);water=d.vec3f(.13,.26,.34);}
 if(biome===6){sky=d.vec3f(.085,.15,.20);far=d.vec3f(.43,.45,.22);hill=d.vec3f(.39,.34,.15);earth=d.vec3f(.43,.35,.18);leaf=d.vec3f(.24,.19,.07);}
 if(biome===7){sky=d.vec3f(.105,.14,.22);far=d.vec3f(.30,.32,.22);hill=d.vec3f(.30,.31,.16);earth=d.vec3f(.4,.35,.19);leaf=d.vec3f(.11,.16,.075);}
 if(biome===8){sky=d.vec3f(.055,.067,.18);far=d.vec3f(.29,.32,.43);hill=d.vec3f(.27,.34,.4);earth=d.vec3f(.35,.37,.35);leaf=d.vec3f(.13,.19,.245);water=d.vec3f(.26,.40,.49);}
 if(biome===9){sky=d.vec3f(.045,.085,.15);far=d.vec3f(.47,.52,.43);hill=d.vec3f(.30,.40,.31);earth=d.vec3f(.45,.48,.36);leaf=d.vec3f(.04,.10,.07);}
 if(biome===10){sky=d.vec3f(.16,.14,.20);far=d.vec3f(.34,.25,.22);hill=d.vec3f(.27,.23,.18);earth=d.vec3f(.35,.29,.20);leaf=d.vec3f(.12,.095,.07);water=d.vec3f(.48,.10,.035);}
 const night=weather===3;
 const shade=std.select(d.f32(1),d.f32(.78),night);
 const noise=fbm2o(uv.mul(d.vec2f(19,25)).add(seed));
 let col=sky.mul(.90+noise*.18);
 // A quiet gilt disk and drawn cloud scrolls, no atmospheric bloom.
 const sun=d.vec2f(.69+.07*std.sin(seed),.85);
 const aspect=std.fwidth(uv.y)/std.max(std.fwidth(uv.x),.00001);
 const sunD=std.length(uv.sub(sun).mul(d.vec2f(aspect,1)));
 col=std.mix(col,ink,1-std.smoothstep(.034,.034+aa,sunD));
 col=std.mix(col,std.select(d.vec3f(.74,.47,.14),paper,night),1-std.smoothstep(.028,.028+aa,sunD));
 const cloudP=d.vec2f(uv.x*3+seed*.04,uv.y*11);
 const cloudCell=std.floor(cloudP);
 const cloudQ=std.fract(cloudP);
 const cloudLine=std.abs(cloudQ.y-.42-std.sin(cloudQ.x*6.283)*.07);
 const clouds=(1-std.smoothstep(.012,.012+aa*11,cloudLine))*std.smoothstep(.05,.22,cloudQ.x)*(1-std.smoothstep(.72,.95,cloudQ.x))*std.step(.6,rnd2(cloudCell))*std.step(.68,uv.y);
 col=std.mix(col,paper,clouds*.36);
 // Four unequal contour bands. Engraving follows each slope, not a square grid.
 for(let layer=0;layer<4;layer++){
  const l=d.f32(layer);
  const x=uv.x+travel*.0015*(l+1);
  const n=fbm2o(d.vec2f(x*(3.2+l*.7)+seed,l*7.3+seed));
  const tooth=std.abs(std.sin(x*(12+l*3)+seed));
  const crag=std.select(n,tooth*.36+n*.62,biome===4||biome===8||biome===10);
  const h=.59-l*.069+crag*(.18-l*.018);
  const ridge=1-std.smoothstep(h-aa,h+aa,uv.y);
  const tone=std.mix(far,hill,l*.25).mul(.95+noise*.13);
  col=std.mix(col,tone,ridge);
  const edge=1-std.smoothstep(aa*.5,aa*1.5,std.abs(uv.y-h));
  col=std.mix(col,ink,edge*.7);
  const engraving=(uv.y-h)*88+(uv.x*7+std.sin(uv.x*33+seed))*.21;
  const hatch=1-std.smoothstep(.018,.018+aa*75,std.abs(std.fract(engraving)-.5));
  col=std.mix(col,ink,hatch*ridge*std.smoothstep(.012,.04,h-uv.y)*(1-std.smoothstep(.10,.14,h-uv.y))*.24);
  if(biome===4||biome===9)col=std.mix(col,paper,ridge*(1-std.smoothstep(.006,.038,h-uv.y))*.8);
 }
 // Architecture belongs to the shared SVG vocabulary. A second, faded GPU
 // town used to double-expose the real settlement and had no event meaning.
 // The foreland's warm path winds into the picture; quiet space under the actor
 // leaves their boots and contact stamps readable.
 const depth=std.clamp((.51-uv.y)/.51,0,1);
 const land=1-std.smoothstep(.47,.52,uv.y);
 const groundNoise=fbm2o(d.vec2f(uv.x*21+seed,depth*17+travel*.45));
 col=std.mix(col,earth.mul(.92+groundNoise*.15),land);
 const routeCenter=.5+std.sin(depth*7.4+seed*.045)*.105*std.sin(depth*3.14159);
 const roadW=.014+depth*depth*.18;
 const pathD=std.abs(uv.x-routeCenter)-roadW;
 const path=1-std.smoothstep(-aa,aa,pathD);
 const border=1-std.smoothstep(aa,aa*2.4,std.abs(pathD));
 col=std.mix(col,ink,border*land*.8);
 col=std.mix(col,paper.mul(.91+noise*.07),path*land);
 const rut=1-std.smoothstep(.001,.001+aa,std.abs(std.abs(uv.x-routeCenter)-roadW*.64));
 col=std.mix(col,earth,rut*path*land*.33);
 const pebbleCell=std.floor(d.vec2f(uv.x*82,uv.y*95+travel*.8));
 const pebbleQ=std.fract(d.vec2f(uv.x*82,uv.y*95+travel*.8)).sub(.5);
 const pebble=(1-std.smoothstep(.055,.15,std.length(pebbleQ.mul(d.vec2f(1,2)))))*std.step(.87,rnd2(pebbleCell));
 col=std.mix(col,ink,pebble*land*path*.45);
 // Connected riverbanks and short pen-wave groups, also the lava course.
 const riverX=.77+std.sin(depth*6.6+seed*.02)*.08;
 const riverW=.013+depth*.055;
 const waterD=std.abs(uv.x-riverX)-riverW;
 const river=(1-std.smoothstep(-aa,aa,waterD))*land*(1-path);
 if(biome===0||biome===2||biome===5||biome===10){
  col=std.mix(col,ink,(1-std.smoothstep(aa,aa*2,std.abs(waterD)))*land*(1-path));
  col=std.mix(col,water,river);
  const wave=uv.y*114+std.sin(uv.x*34+seed)*.3;
  const waveLine=1-std.smoothstep(.018,.018+aa*82,std.abs(std.fract(wave)-.5));
  const broken=std.smoothstep(.2,.7,vnoise(d.vec2f(uv.x*39,uv.y*21+seed)));
  col=std.mix(col,paper,waveLine*broken*river*.65);
 }
 // Mist belongs behind the trees, never across their trunks.
 if(weather===2&&biome!==11)col=std.mix(col,paper,std.exp(-std.pow((uv.y-.46)/.055,2))*.10);
 // Small grouped trees on the middle slopes. Per-cell silhouettes have hand-like
 // branching and leaf lobes; no floating circles or conical placeholder forest.
 for(let row=0;row<3;row++){
  const l=d.f32(row);
  const count=10+l*3;
  const coordinate=uv.x*count+seed*.2+travel*.006*(l+1);
  const cell=std.floor(coordinate);
  const r=rnd2(d.vec2f(cell+seed,l*11));
  const base=.44-l*.052+r*.024;
  const size=(.10+l*.018)*(.83+r*.34);
  const cellCenter=.25+r*.5;
  const rootX=(cell+cellCenter-seed*.2-travel*.006*(l+1))/count;
  const q=d.vec2f((std.fract(coordinate)-cellCenter)/count,uv.y-base).div(size);
  const kind=std.select(std.select(d.f32(0),d.f32(1),biome===4||biome===8||biome===9),d.f32(2),biome===7||biome===10);
  const shape=landscapeTree(q,cell+seed,kind);
  const localAA=aa/size;
  // Visibility is a whole-tree decision at its root, not a per-pixel alpha
  // ramp that erased half of a trunk near the actor lane.
  const outsideLane=std.step(.20,std.abs(rootX-.5));
  const show=std.step(std.select(d.f32(.37),d.f32(.78),biome===3||biome===5),r)*std.mix(d.f32(1),outsideLane,std.clamp(l,0,1));
  col=std.mix(col,ink,(1-std.smoothstep(localAA,localAA*2,shape.x))*show);
  col=std.mix(col,leaf.mul(.85+r*.32),(1-std.smoothstep(-localAA,localAA,shape.x))*show);
  const leafLines=std.abs(std.sin((q.x*.6+q.y)*58+seed))<.09;
  col=std.mix(col,paper,std.select(d.f32(0),d.f32(.14),leafLines)*(1-std.smoothstep(-localAA,localAA,shape.x))*show);
  col=std.mix(col,ink,(1-std.smoothstep(-localAA,localAA,shape.y))*show);
  const rootMark=std.length(d.vec2f(q.x/.13,(q.y+.008)/.022))-1;
  col=std.mix(col,ink,(1-std.smoothstep(localAA,localAA*2,rootMark))*show*.75);
 }
 if(biome===11){
  // Five receding vaulted bays, with masonry courses and a worn central aisle.
  const x=uv.x-.5;
  col=d.vec3f(.11,.16,.15).mul(.86+noise*.18);
  for(let bay=0;bay<5;bay++){
   const k=d.f32(bay);
   const s=.48/(1+k*.51);
   const y=uv.y-.46;
   const ring=std.abs(std.length(d.vec2f(x,y))-.97*s)-.033*s;
   const shaft=std.max(std.abs(std.abs(x)-.91*s)-.043*s,std.max(.09-uv.y,uv.y-.48));
   const arch=std.min(shaft,std.max(ring,-y));
   col=std.mix(col,ink,1-std.smoothstep(aa,aa*2,arch));
   col=std.mix(col,d.vec3f(.28,.33,.27).mul(1-k*.075),1-std.smoothstep(-aa,aa,arch));
   const course=std.abs(std.fract(uv.y*(14+k*3))-.5);
   col=std.mix(col,ink,(1-std.smoothstep(.016,.016+aa*15,course))*(1-std.smoothstep(-aa,aa,shaft))*.55);
  }
  const aisle=(1-std.smoothstep(.03+depth*.18,.033+depth*.18,std.abs(x)))*land;
  col=std.mix(col,paper.mul(.59+noise*.07),aisle);
  const floorLines=std.abs(std.fract((1-uv.y)*24)-.5);
  col=std.mix(col,ink,(1-std.smoothstep(.02,.02+aa*20,floorLines))*aisle*.25);
 }
 // Weather remains part of the saved encounter rules. The enclosed crypt
 // does not receive the outdoor precipitation pass across its ceiling/aisle.
 if(weather===1&&biome!==11){
  const rainX=uv.x*71+uv.y*9;
  const rainColumn=std.floor(rainX);
  const rainOffset=rnd2(d.vec2f(rainColumn,seed+83));
  const rainY=uv.y*17-time*.68+rainOffset*11;
  const streak=1-std.smoothstep(.018,.018+aa*65,std.abs(std.fract(rainX)-.5));
  const dash=std.smoothstep(.74,.80,std.fract(rainY))*(1-std.smoothstep(.93,.99,std.fract(rainY)))*std.step(.22,rainOffset);
  col=std.mix(col,paper,streak*dash*.14);
 }
 return col.mul(shade).mul(.985+rnd2(std.floor(px))*.03);
};
